import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Tooltip,
} from '@mui/material';
import { Add, EventNote, Check, Close, Pause, PlayArrow, Assignment, Person, CalendarMonth } from '@mui/icons-material';
import { leaveAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';

const LeaveRequests = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allLeaves, setAllLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
    status: 'approved',
  });
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const leaveRes = await leaveAPI.getAll();
      if (leaveRes.data.success) {
        setAllLeaves(leaveRes.data.data.leaves);
      }
      
      const empRes = await employeesAPI.getAll({ status: 'active' });
      if (empRes.data.success) {
        const empList = empRes.data.data.employees || empRes.data.data;
        setEmployees(empList);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeFilter = (employeeId) => {
    setSelectedEmployee(employeeId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return { bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#81C784', border: '1px solid #81C784' };
      case 'pending':
        return { bgcolor: 'rgba(255, 152, 0, 0.2)', color: '#FFB74D', border: '1px solid #FFB74D' };
      case 'rejected':
        return { bgcolor: 'rgba(244, 67, 54, 0.2)', color: '#EF9A9A', border: '1px solid #EF9A9A' };
      case 'on_hold':
        return { bgcolor: 'rgba(33, 150, 243, 0.2)', color: '#90CAF9', border: '1px solid #90CAF9' };
      case 'cancelled':
        return { bgcolor: 'rgba(100, 100, 100, 0.2)', color: '#B4C4A8', border: '1px solid #B4C4A8' };
      default:
        return { bgcolor: 'rgba(100, 100, 100, 0.2)', color: '#B4C4A8', border: '1px solid #B4C4A8' };
    }
  };

  const getLeaveTypeIcon = (type) => {
    switch (type) {
      case 'casual':
        return <EventNote sx={{ fontSize: 16, color: '#D4AF37' }} />;
      case 'sick':
        return <EventNote sx={{ fontSize: 16, color: '#FFB74D' }} />;
      case 'paid':
        return <EventNote sx={{ fontSize: 16, color: '#81C784' }} />;
      default:
        return <EventNote sx={{ fontSize: 16, color: '#B4C4A8' }} />;
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await leaveAPI.approve(leaveId);
      setSuccess('Leave approved successfully');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    const result = await Swal.fire({
      title: 'Reject Leave',
      text: 'Enter rejection reason:',
      input: 'text',
      inputPlaceholder: 'Rejection reason',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      cancelButtonText: 'Cancel',
      background: 'rgb(10, 25, 41)',
      color: '#D4AF37',
      confirmButtonColor: '#D4AF37',
      cancelButtonColor: '#EF4444',
    });
    if (result.isConfirmed && result.value) {
      try {
        await leaveAPI.reject(leaveId, { reason: result.value });
        setSuccess('Leave rejected');
        loadData();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to reject leave');
      }
    }
  };

  const handleHold = async (leaveId) => {
    try {
      await leaveAPI.hold(leaveId);
      setSuccess('Leave put on hold');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to hold leave');
    }
  };

  const handleReleaseHold = async (leaveId) => {
    try {
      await leaveAPI.releaseHold(leaveId);
      setSuccess('Leave released from hold');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to release leave from hold');
    }
  };

  const handleApplyLeave = async () => {
    setError('');
    if (!formData.employee_id) {
      setError('Please select an employee');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      setError('Please select start and end dates');
      return;
    }
    try {
      const response = await leaveAPI.createForEmployee(formData);
      if (response.data.success) {
        setSuccess('Leave created successfully for employee');
        setShowApplyDialog(false);
        setFormData({
          employee_id: '',
          leave_type: 'casual',
          start_date: '',
          end_date: '',
          reason: '',
          is_half_day: false,
          status: 'approved',
        });
        loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create leave');
    }
  };

  const getDisplayLeaves = () => {
    let displayLeaves = allLeaves;
    
    if (selectedEmployee) {
      displayLeaves = displayLeaves.filter(l => l.employee_id === selectedEmployee);
    }
    
    if (tabValue === 0) return displayLeaves.filter(l => l.status === 'pending');
    if (tabValue === 1) return displayLeaves.filter(l => l.status === 'approved');
    if (tabValue === 2) return displayLeaves.filter(l => l.status === 'rejected');
    if (tabValue === 3) return displayLeaves.filter(l => l.status === 'on_hold');
    return displayLeaves;
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const pendingCount = allLeaves.filter(l => l.status === 'pending').length;
  const approvedCount = allLeaves.filter(l => l.status === 'approved').length;
  const rejectedCount = allLeaves.filter(l => l.status === 'rejected').length;
  const onHoldCount = allLeaves.filter(l => l.status === 'on_hold').length;

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            <Assignment sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle' }} />
            Leave Requests Management
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => setShowApplyDialog(true)}
            sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
          >
            Add Leave for Employee
          </Button>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2, 
              bgcolor: 'rgb(10, 25, 41)', 
              color: '#EF9A9A',
              border: '1px solid #EF9A9A',
              '& .MuiAlert-icon': { color: '#EF9A9A' }
            }} 
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2, 
              bgcolor: 'rgb(10, 25, 41)', 
              color: '#81C784',
              border: '1px solid #81C784',
              '& .MuiAlert-icon': { color: '#81C784' }
            }} 
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255, 152, 0, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#FFB74D'
                }}>
                  <EventNote sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#FFB74D', fontWeight: 700 }}>
                  {pendingCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(76, 175, 80, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#81C784'
                }}>
                  <Check sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#81C784', fontWeight: 700 }}>
                  {approvedCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  Approved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(244, 67, 54, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#EF9A9A'
                }}>
                  <Close sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#EF9A9A', fontWeight: 700 }}>
                  {rejectedCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  Rejected
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: 'rgb(10, 25, 41)', 
              border: '1px solid #D4AF37',
              borderRadius: 3,
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }
            }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(33, 150, 243, 0.2)', 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 1,
                  color: '#90CAF9'
                }}>
                  <Pause sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" sx={{ color: '#90CAF9', fontWeight: 700 }}>
                  {onHoldCount}
                </Typography>
                <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                  On Hold
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ 
          mb: 3, 
          p: 2, 
          bgcolor: 'rgb(10, 25, 41)', 
          border: '1px solid #D4AF37',
          borderRadius: 3 
        }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel sx={{ color: '#B4C4A8' }}>Filter by Employee</InputLabel>
              <Select
                value={selectedEmployee}
                label="Filter by Employee"
                onChange={(e) => handleEmployeeFilter(e.target.value)}
                sx={{ 
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C19B2E' }
                }}
              >
                <MenuItem value="">All Employees</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tabs 
              value={tabValue} 
              onChange={(e, v) => setTabValue(v)} 
              sx={{
                flexGrow: 1,
                '& .MuiTab-root': { color: '#B4C4A8' },
                '& .Mui-selected': { color: '#D4AF37' },
                '& .MuiTabs-indicator': { bgcolor: '#D4AF37' }
              }}
            >
              <Tab label={`Pending (${pendingCount})`} />
              <Tab label={`Approved (${approvedCount})`} />
              <Tab label={`Rejected (${rejectedCount})`} />
              <Tab label={`On Hold (${onHoldCount})`} />
            </Tabs>
          </Box>
        </Paper>

        {/* Leave Requests Table */}
        <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2A3E24' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Dates</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Days</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Reason</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getDisplayLeaves().map((leave) => (
                    <TableRow key={leave.id} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', fontSize: '0.875rem' }}>
                            {leave.leave_employee?.first_name?.[0]}{leave.leave_employee?.last_name?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                              {leave.leave_employee ? `${leave.leave_employee.first_name || ''} ${leave.leave_employee.last_name || ''}`.trim() : (leave.employee_name || '-')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                              {leave.leave_employee?.employee_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getLeaveTypeIcon(leave.leave_type)}
                          <Typography sx={{ textTransform: 'capitalize', color: '#FFFFFF' }}>
                            {leave.leave_type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarMonth sx={{ fontSize: 14, color: '#D4AF37' }} />
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                            {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={leave.total_days} 
                          sx={{ 
                            bgcolor: 'rgba(212,175,55,0.2)', 
                            color: '#D4AF37',
                            border: '1px solid #D4AF37',
                            fontWeight: 500
                          }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={leave.status} 
                          sx={getStatusColor(leave.status)} 
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={leave.reason || 'No reason provided'}>
                          <Typography variant="body2" sx={{ color: '#B4C4A8', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {leave.reason || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {leave.status === 'pending' && (
                            <>
                              <Tooltip title="Approve Leave">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleApprove(leave.id)} 
                                  sx={{ color: '#81C784', '&:hover': { bgcolor: 'rgba(129, 199, 132, 0.1)' } }}
                                >
                                  <Check />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Leave">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleReject(leave.id)} 
                                  sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Hold Leave">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleHold(leave.id)} 
                                  sx={{ color: '#90CAF9', '&:hover': { bgcolor: 'rgba(144, 202, 249, 0.1)' } }}
                                >
                                  <Pause />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {leave.status === 'on_hold' && (
                            <>
                              <Tooltip title="Approve Leave">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleApprove(leave.id)} 
                                  sx={{ color: '#81C784', '&:hover': { bgcolor: 'rgba(129, 199, 132, 0.1)' } }}
                                >
                                  <Check />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Leave">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleReject(leave.id)} 
                                  sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Release Hold">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleReleaseHold(leave.id)} 
                                  sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } }}
                                >
                                  <PlayArrow />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {getDisplayLeaves().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography sx={{ color: '#B4C4A8' }}>No leave records found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Add Leave Dialog */}
        <Dialog 
          open={showApplyDialog} 
          onClose={() => setShowApplyDialog(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            Add Leave for Employee
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel sx={{ color: '#B4C4A8' }}>Select Employee</InputLabel>
              <Select
                value={formData.employee_id}
                label="Select Employee"
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                sx={{ 
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' }
                }}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField 
              select 
              fullWidth 
              label="Leave Type" 
              value={formData.leave_type} 
              onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })} 
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            >
              <MenuItem value="casual">Casual Leave</MenuItem>
              <MenuItem value="sick">Sick Leave</MenuItem>
              <MenuItem value="paid">Paid Leave</MenuItem>
              <MenuItem value="unpaid">Unpaid Leave</MenuItem>
            </TextField>
            <TextField 
              fullWidth 
              label="Start Date" 
              type="date" 
              value={formData.start_date} 
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} 
              InputLabelProps={{ shrink: true, sx: { color: '#B4C4A8' } }}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField 
              fullWidth 
              label="End Date" 
              type="date" 
              value={formData.end_date} 
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} 
              InputLabelProps={{ shrink: true, sx: { color: '#B4C4A8' } }}
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
            <TextField 
              select 
              fullWidth 
              label="Status" 
              value={formData.status} 
              onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
            <TextField 
              fullWidth 
              label="Reason" 
              multiline 
              rows={3} 
              value={formData.reason} 
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              InputLabelProps={{ sx: { color: '#B4C4A8' } }}
              InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setShowApplyDialog(false)} 
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleApplyLeave}
              sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
            >
              Create Leave
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default LeaveRequests;